// ABOUTME: White card of the auth screens — title row with the language switcher, content, footer links.
// ABOUTME: Below sm the card drops its frame and the content runs edge to edge, per the design.

import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { CSelect } from "@shared/ui/CSelect"
import { LangChange, LangCurrent, LangList } from "@/locales"
import { TAB_STORAGE_KEYS } from "@shared/constants/storageKeys"

// Re-exported from features/auth so both the pages and FlowForm share one divider (was duplicated).
export { AuthDivider } from "@/features/auth/AuthDivider"

const AuthLanguageSwitcher = () => {
  useTranslation() // re-render on language change
  const onChange = (e: { target: { value: string } }) => {
    void LangChange(e.target.value)
    // Remember the explicit choice so it survives login (goToApp forwards it as ?lang,
    // which LangLoad applies + persists to the account — otherwise the account language
    // would override the language the user picked on the login screen).
    try {
      sessionStorage.setItem(TAB_STORAGE_KEYS.AUTH_SELECTED_LANG, e.target.value)
    } catch {
      /* ignore */
    }
  }
  return (
    <CSelect
      value={LangCurrent()}
      onChange={onChange}
      className="w-auto shrink-0 font-medium"
    >
      {LangList().map((lang) => (
        <option
          key={lang}
          value={lang}
        >
          {lang.toUpperCase()}
        </option>
      ))}
    </CSelect>
  )
}

interface AuthCardProps {
  title: string
  children: ReactNode
  /** Links under the hairline rule. */
  footer?: ReactNode
  /** Two links go to the edges, a single one is centred. */
  footerSplit?: boolean
}

export const AuthCard = ({ title, children, footer, footerSplit }: AuthCardProps) => (
  <div className="flex w-full max-w-[400px] flex-col gap-4 rounded-xl bg-white p-6 shadow-modalShadow max-sm:max-w-none max-sm:rounded-none max-sm:px-0 max-sm:pt-2 max-sm:pb-7 max-sm:shadow-none">
    <div className="flex items-center justify-between gap-3">
      <h1 className="text-xl font-bold break-words max-sm:text-[22px]">{title}</h1>
      <AuthLanguageSwitcher />
    </div>

    {children}

    {footer && (
      <div
        className={`mt-1 flex items-center gap-3 border-t border-border-faint pt-3 text-sm ${
          footerSplit ? "justify-between" : "justify-center"
        }`}
      >
        {footer}
      </div>
    )}
  </div>
)
